import { Flood } from "@common/definitions/storm";
import { type FloodField } from "@common/utils/flood";
import { type Vector } from "@common/utils/vector";
import { BufferImageSource, GlProgram, Mesh, MeshGeometry, Shader, Texture } from "pixi.js";
import { PIXI_SCALE } from "../utils/constants";
import { createNoiseTexture } from "../war/warNoise";

/*
    The flood's water, drawn by the graphics card (0.98.5, the owner of 0.98.4's: "the river bnreaking over its banks,
    well trhats hideous, it doesnt look like water, it looks like the map being painted in blue, also, judging from the
    picture i sent, it sdoesnt start from the river"): one quad over the screen in the ground floor's container, its
    shader reading the flood's field (utils/flood.ts: how far each cell is from the river's water, and how far the water
    may spread to reach it) from a small texture, so the water lies exactly where the server says it does and grows out
    of the river itself: the river's own water swollen and choppy first, then the flood coming over the banks, deep and
    dark near the river, shallow and silty at its edge, where the ground shows through it. Its edge wanders (noise
    along it, no cell's straight line), with foam broken along it and a dark wet band on the ground just past it; the
    surface moves (two drifts of ripples, glints where they cross, the current's streaks running away from the river),
    the rain rings on it. The server's own cells (8 units) decide who wades; this is their look.

    Where the shaders aren't drawn (the Low look, WebGPU, software rendering) the storm view keeps its plain water.
*/

/** the field as a texture: R,G the distance from the river (sixteen bits, in 32nds of a unit), B its cap's class, A the flow's angle */
const CLASS_STEP = 60;
const DISTANCE_SCALE = 32;

export const FLOOD_VERTEX = /* glsl */`
in vec2 aPosition;
in vec2 aUV;
out vec2 vWorld;
out vec4 vColor;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
uniform vec4 uColor;

void main(void) {
    mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
    gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
    vWorld = aUV;
    // (the container's own colour and fade, as Pixi gives every mesh: the ground floor fades going down to a basement)
    vColor = uColor;
}
`;

// (the source begins with its precision: Pixi keeps it, and the sixteen-bit distance needs highp's exactness; the
// shader is GLSL ES 1.0 as the war look's are: Pixi rewrites in, out, texture and finalColor for WebGL1)
export const FLOOD_FRAGMENT = /* glsl */`precision highp float;
in vec2 vWorld;
in vec4 vColor;
out vec4 finalColor;

uniform sampler2D uField;
uniform sampler2D uNoise;
uniform vec2 uFieldSize;
uniform float uCell;
uniform float uLevel;
uniform float uCapNorth;
uniform float uCapEast;
uniform float uTime;
uniform float uRiverAlpha;
uniform vec2 uWind;
// (0.98.6) the way toward the light on screen (the lighting's sun: the ripples' lit sides face it), and the lightning's
// flash now (0 none): the water flashes and glints with it
uniform vec2 uLightDir;
uniform float uFlash;

const float TAU = 6.2831853;

// (the two bytes scaled apart before they're summed: the sum stays in the units' thousands, exact even where the
// card gives the fragment mediump for all the asking)
float distanceOf(vec4 t) {
    return t.r * ${(255 * 256 / DISTANCE_SCALE).toFixed(4)} + t.g * ${(255 / DISTANCE_SCALE).toFixed(6)};
}

float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
}

float capOf(vec4 t) {
    float c = floor(t.b * 255.0 / ${CLASS_STEP.toFixed(1)} + 0.5);
    return c < 0.5 ? -1.0 : (c < 1.5 ? 1.0e9 : (c < 2.5 ? uCapNorth : uCapEast));
}

void main(void) {
    vec2 w = vWorld;
    vec2 inv = 1.0 / uFieldSize;
    // the distance from the river, between the cells (their centres), so the edge isn't a stair of cells
    vec2 at = w / uCell - 0.5;
    vec2 c0 = floor(at);
    vec2 f = at - c0;
    float d00 = distanceOf(texture(uField, (c0 + 0.5) * inv));
    float d10 = distanceOf(texture(uField, (c0 + vec2(1.5, 0.5)) * inv));
    float d01 = distanceOf(texture(uField, (c0 + vec2(0.5, 1.5)) * inv));
    float d11 = distanceOf(texture(uField, (c0 + vec2(1.5, 1.5)) * inv));
    float d = mix(mix(d00, d10, f.x), mix(d01, d11, f.x), f.y);
    // the cell's own cap and flow (its own: the caps don't blend)
    vec4 own = texture(uField, (floor(w / uCell) + 0.5) * inv);
    float cap = capOf(own);
    float river = step(cap, -0.5);
    float angle = own.a * TAU - 3.14159265;
    vec2 flow = vec2(cos(angle), sin(angle)) * (1.0 - river);

    // the edge wanders: the distance warped by two grains of noise
    vec4 g1 = texture(uNoise, w * 0.031 + 0.17);
    vec4 g2 = texture(uNoise, w * 0.12 + 0.61);
    float wander = (g1.r - 0.5) * 3.2 + (g2.b - 0.5) * 1.1;
    float reach = min(uLevel, cap);
    // how far past this spot the water has come (under 0: dry, that far short of it); the river deep as the level
    float edge = mix(reach - (d + wander), uLevel + 12.0, river);

    // nothing to draw well clear of the water
    if (edge < -4.5) {
        finalColor = vec4(0.0);
        return;
    }

    float under = smoothstep(-0.5, 0.5, edge);
    float depth = mix(clamp(edge / 30.0, 0.0, 1.0), 1.0, river);

    // the water's colours: silty and pale where it's shallow, murky in the middle, dark and cold where it's deep
    vec3 shallow = vec3(0.46, 0.44, 0.33);
    vec3 murk = vec3(0.28, 0.32, 0.28);
    vec3 deep = vec3(0.13, 0.165, 0.17);
    vec3 col = mix(shallow, murk, smoothstep(0.0, 0.2, depth));
    col = mix(col, deep, smoothstep(0.2, 1.0, depth));

    // the surface: two drifts of ripples (with the current, and with the wind), a fine grain over them, glints where
    // their crests cross
    vec2 drift = flow * 0.9 + uWind * 0.35;
    vec2 d1 = w * 0.11 + drift * uTime * 0.05 + vec2(uTime * 0.011, -uTime * 0.008);
    vec2 d2 = w * 0.23 - drift * uTime * 0.03 + vec2(-uTime * 0.013, uTime * 0.015);
    vec4 s1 = texture(uNoise, d1);
    vec4 s2 = texture(uNoise, d2);
    float ripple = s1.r * 0.55 + s2.r * 0.45;
    float grain = texture(uNoise, w * 0.4 + vec2(uTime * 0.02, uTime * 0.03)).b;
    col *= 0.84 + 0.32 * ripple + 0.06 * (grain - 0.5);
    // lit from the sun's side (0.98.6: the lighting's; the top-left by the storm's grey): each ripple's near side
    // brighter, its far side darker (strong enough to read under the storm's dark grade, over a flat floor with
    // nothing standing in it); the lightning's flash whitens the crests and throws glints all over it
    vec2 l1 = uLightDir * 0.02;
    float lit = (texture(uNoise, d1 + l1).r - s1.r) * 2.4 + (texture(uNoise, d2 + l1 * 1.4).r - s2.r) * 1.2;
    col *= 1.0 + clamp(lit, -1.0, 1.0) * (0.42 + 0.5 * uFlash);
    float glint = smoothstep(0.8 - 0.2 * uFlash, 0.94, s1.b * 0.5 + s2.b * 0.5);
    col += vec3(0.22, 0.24, 0.24) * glint * (0.4 + 0.6 * depth) * (1.0 + 2.5 * uFlash);

    // the foam: broken along the edge, and a few flecks on the water near it
    float band = smoothstep(1.8, 0.0, edge) * smoothstep(-0.9, 0.1, edge);
    float fn = texture(uNoise, w * 0.27 + vec2(uTime * 0.05, -uTime * 0.03)).b;
    float foam = band * smoothstep(0.38, 0.66, fn + 0.22 * band);
    foam += smoothstep(4.0, 0.5, edge) * smoothstep(0.86, 0.95, fn) * under * 0.7;
    // (a second, fainter line of it a few steps in: the last wave's)
    float band2 = smoothstep(6.0, 4.2, edge) * smoothstep(2.6, 3.6, edge);
    foam += band2 * smoothstep(0.55, 0.8, texture(uNoise, w * 0.19 + vec2(-uTime * 0.04, uTime * 0.02)).b) * 0.45;
    col = mix(col, vec3(0.88, 0.91, 0.90), clamp(foam, 0.0, 1.0) * 0.85);

    // the rain: small rings widening on the water here and there
    vec2 cellId = floor(w / 4.0);
    vec3 hh = vec3(hash12(cellId), hash12(cellId + 17.3), hash12(cellId + 41.7));
    vec2 centre = (cellId + 0.2 + 0.6 * hh.xy) * 4.0;
    float cycle = uTime * 1.1 + hh.x * 7.0;
    float phase = fract(cycle);
    float ringOn = step(0.74, fract(hh.z + floor(cycle) * 0.37));
    float ring = abs(length(w - centre) - phase * 1.2);
    col += vec3(0.2) * smoothstep(0.14, 0.0, ring) * (1.0 - phase) * ringOn * under;

    // how much of the ground shows: more at the shallow edge; over the river's own water, as much as asked (the river
    // cells reach a little past its bank: drawn as deep as the flood beside them, so no seam shows along it)
    float alpha = under * mix(0.6, 0.86, smoothstep(0.0, 0.2, depth));
    alpha = mix(alpha, under * uRiverAlpha, river);
    // the wet band: the ground dark just past the edge
    float wet = smoothstep(-4.0, 0.0, edge) * (1.0 - under) * 0.32;
    vec3 wetCol = vec3(0.09, 0.085, 0.07);
    float a = alpha + wet * (1.0 - alpha);
    vec3 c = col * alpha + wetCol * wet * (1.0 - alpha);
    finalColor = vec4(c, a) * vColor;
}
`;

let noise: Texture | undefined;
const noiseTexture = (): Texture => noise ??= createNoiseTexture();

/** the field's cells as texels: the distance in sixteen bits, the cap's class, the flow's angle */
export function fieldTexels(field: FloodField): Uint8Array {
    const { columns, rows } = field;
    const data = new Uint8Array(columns * rows * 4);
    for (let row = 0; row < rows; row++) {
        for (let column = 0; column < columns; column++) {
            const i = (row * columns + column) * 4;
            const distance = Math.min(field.cellDistance(column, row), 65535 / DISTANCE_SCALE);
            const q = Math.round(distance * DISTANCE_SCALE);
            data[i] = q >> 8;
            data[i + 1] = q & 255;
            const cap = field.cellCap(column, row);
            const kind = cap < 0 ? 0 : cap === Infinity ? 1 : cap === Flood.reachNorth ? 2 : 3;
            data[i + 2] = kind * CLASS_STEP;
            const flow = field.flowAt({ x: (column + 0.5) * field.cell, y: (row + 0.5) * field.cell });
            const angle = Math.atan2(flow.y, flow.x);
            data[i + 3] = Math.round((angle + Math.PI) / (2 * Math.PI) * 255);
        }
    }
    return data;
}

/** the flood's water on the graphics card: the quad over the screen and its shader */
export class FloodWater {
    readonly mesh: Mesh<MeshGeometry, Shader>;
    private readonly _field: Texture;
    private readonly _geometry: MeshGeometry;
    private readonly _uniforms: Record<string, unknown>;

    constructor(field: FloodField, wind: Vector) {
        const texels = fieldTexels(field);
        this._field = new Texture({
            source: new BufferImageSource({
                resource: texels,
                width: field.columns,
                height: field.rows,
                format: "rgba8unorm",
                scaleMode: "nearest",
                addressMode: "clamp-to-edge",
                alphaMode: "no-premultiply-alpha"
            })
        });
        this._geometry = new MeshGeometry({
            positions: new Float32Array(8),
            uvs: new Float32Array(8),
            indices: new Uint32Array([0, 1, 2, 0, 2, 3])
        });
        this._geometry.batchMode = "no-batch";
        const shader = new Shader({
            glProgram: GlProgram.from({ vertex: FLOOD_VERTEX, fragment: FLOOD_FRAGMENT, name: "flood-water" }),
            resources: {
                uField: this._field.source,
                uNoise: noiseTexture().source,
                floodUniforms: {
                    uFieldSize: { value: new Float32Array([field.columns, field.rows]), type: "vec2<f32>" },
                    uCell: { value: field.cell, type: "f32" },
                    uLevel: { value: 0, type: "f32" },
                    uCapNorth: { value: Flood.reachNorth, type: "f32" },
                    uCapEast: { value: Flood.reachEast, type: "f32" },
                    uTime: { value: 0, type: "f32" },
                    uRiverAlpha: { value: 0, type: "f32" },
                    uWind: { value: new Float32Array([wind.x, wind.y]), type: "vec2<f32>" },
                    uLightDir: { value: new Float32Array([-0.7, -0.7]), type: "vec2<f32>" },
                    uFlash: { value: 0, type: "f32" }
                }
            }
        });
        this._uniforms = shader.resources.floodUniforms.uniforms as Record<string, unknown>;
        this.mesh = new Mesh({ geometry: this._geometry, shader });
        this.mesh.eventMode = "none";
    }

    /**
     * the quad over the screen's view (game units) this frame, the water's spread as drawn, the time, and (0.98.6) the
     * light: the way toward it on screen and the lightning's flash now
     */
    update(view: { readonly min: Vector, readonly max: Vector }, level: number, time: number, light?: { readonly x: number, readonly y: number, readonly flash: number }): void {
        const { min, max } = view;
        const s = PIXI_SCALE;
        const positions = this._geometry.positions;
        positions.set([min.x * s, min.y * s, max.x * s, min.y * s, max.x * s, max.y * s, min.x * s, max.y * s]);
        this._geometry.positions = positions;
        const uvs = this._geometry.uvs;
        uvs.set([min.x, min.y, max.x, min.y, max.x, max.y, min.x, max.y]);
        this._geometry.uvs = uvs;
        const u = this._uniforms;
        u.uLevel = level;
        u.uTime = time;
        // (the river's own water takes the look as the flood begins: swollen and choppy before it's over the banks)
        u.uRiverAlpha = 0.86 * Math.min(1, level / 5);
        if (light) {
            (u.uLightDir as Float32Array)[0] = light.x;
            (u.uLightDir as Float32Array)[1] = light.y;
            u.uFlash = light.flash;
        }
    }

    destroy(): void {
        const shader = this.mesh.shader;
        this.mesh.destroy();
        shader?.destroy();
        this._geometry.destroy(true);
        this._field.destroy(true);
    }
}
